import * as mock from '@/api/mock/adapter'
import { getAccessToken } from '@/auth/token'
import type { AppConfig } from '@/config/runtime'
import { createHttpAdapter } from './http/adapter'
import { createClient } from './http/client'

/**
 * The one place that decides which backend the SPA reads.
 *
 * `apiBaseUrl` set means the Portal API; null means the in-memory mock
 * (runtime.ts). Chosen once, by `ConfigProvider`, before anything below it
 * renders, so no query can run against the wrong one.
 */

/** Session functions are not API calls: they back `MockAuthProvider` only. */
export type Adapter = Omit<typeof mock, 'getSession' | 'signIn' | 'signOut'>

let active: Adapter = mock

export function configureApi(config: Pick<AppConfig, 'apiBaseUrl'>): void {
  active = config.apiBaseUrl
    ? createHttpAdapter(createClient({ baseUrl: config.apiBaseUrl, getToken: getAccessToken }))
    : mock
}

/**
 * Resolves the method on the active adapter at call time, not import time:
 * `queries/` captures references like `api.listPrograms` when its module loads,
 * which is before `configureApi` runs.
 */
export const api = new Proxy({} as Adapter, {
  get:
    (_target, method) =>
    (...args: unknown[]) =>
      (active[method as keyof Adapter] as (...a: unknown[]) => unknown)(...args),
})
